import { useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Field, inputCls } from "@/components/shared";

const LINK_ENDPOINTS = { seller: "/sellers", property: "/properties", buyer: "/buyers", viewing: "/viewings" };

export const FormFillDialog = ({ template, onClose, onSaved }) => {
  const { t } = useLang();
  const [values, setValues] = useState({});
  const [linkedType, setLinkedType] = useState("");
  const [linkedId, setLinkedId] = useState("");
  const [linkedName, setLinkedName] = useState("");
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const setVal = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));

  const loadRecords = async (type) => {
    if (!type || !LINK_ENDPOINTS[type]) return setRecords([]);
    const { data } = await api.get(LINK_ENDPOINTS[type]);
    setRecords(data.map((d) => ({ id: d.id, name: d.name || `${d.buyer_name} – ${d.property_name}` })));
  };

  const uploadFile = async (file, fieldId) => {
    setUploadingId(fieldId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVal(fieldId, data.path);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingId(null);
    }
  };

  const save = async () => {
    const missing = (template.fields || []).find(
      (f) => f.required && (values[f.id] === undefined || values[f.id] === "" || values[f.id] === null)
    );
    if (missing) return toast.error(`${missing.label}: ${t("required")}`);
    setSaving(true);
    try {
      await api.post("/form_submissions", {
        template_id: template.id,
        template_name: template.name,
        linked_type: linkedType,
        linked_id: linkedId,
        linked_name: linkedName,
        values: (template.fields || []).map((f) => ({
          field_id: f.id, label: f.label, type: f.type, value: values[f.id] ?? (f.type === "checkbox" ? false : ""),
        })),
      });
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f, i) => {
    const common = { "data-testid": `fill-field-${i}`, className: inputCls };
    switch (f.type) {
      case "textarea":
        return <textarea {...common} rows={3} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} />;
      case "number":
        return <input {...common} type="number" value={values[f.id] ?? ""} onChange={(e) => setVal(f.id, e.target.value)} />;
      case "date":
        return <input {...common} type="date" value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} />;
      case "dropdown":
        return (
          <Select value={values[f.id] || ""} onValueChange={(v) => setVal(f.id, v)}>
            <SelectTrigger data-testid={`fill-field-${i}`} className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case "checkbox":
        return (
          <div className="pt-1">
            <Checkbox data-testid={`fill-field-${i}`} checked={!!values[f.id]} onCheckedChange={(c) => setVal(f.id, !!c)} />
          </div>
        );
      case "file":
        return (
          <label className={`flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2.5 cursor-pointer hover:border-stone-400 text-sm text-stone-500 transition-colors ${uploadingId === f.id ? "opacity-50" : ""}`}>
            <Upload size={15} strokeWidth={1.5} />
            {values[f.id] ? String(values[f.id]).split("/").pop() : uploadingId === f.id ? "…" : t("typeFile")}
            <input data-testid={`fill-field-${i}`} type="file" accept="image/*,.pdf" className="hidden"
              onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], f.id)} />
          </label>
        );
      default:
        return <input {...common} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} />;
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium text-stone-900">{template.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-stone-50 border border-stone-200 rounded-lg p-4 mt-2">
          <Field label={t("linkedType")}>
            <Select value={linkedType || "none"}
              onValueChange={(v) => {
                const type = v === "none" ? "" : v;
                setLinkedType(type); setLinkedId(""); setLinkedName("");
                loadRecords(type);
              }}>
              <SelectTrigger data-testid="fill-linktype-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("none")}</SelectItem>
                <SelectItem value="seller">{t("seller")}</SelectItem>
                <SelectItem value="property">{t("property")}</SelectItem>
                <SelectItem value="buyer">{t("buyer")}</SelectItem>
                <SelectItem value="viewing">{t("viewing")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("linkedRecord")}>
            <Select value={linkedId || "none"} disabled={!linkedType}
              onValueChange={(v) => {
                const rec = records.find((r) => r.id === v);
                setLinkedId(v === "none" ? "" : v);
                setLinkedName(rec ? rec.name : "");
              }}>
              <SelectTrigger data-testid="fill-linkrecord-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("none")}</SelectItem>
                {records.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="space-y-4 mt-4">
          {(template.fields || []).map((f, i) => (
            <Field key={f.id} label={`${f.label}${f.required ? " *" : ""}`}>
              {renderField(f, i)}
            </Field>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button data-testid="cancel-fill-button" onClick={onClose}
            className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
          <button data-testid="save-fill-button" onClick={save} disabled={saving}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("submitForm")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
